import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { IsShadowMailboxUser } from 'owa-mail-ads-shared/lib/sharedAdsUtils';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import React from 'react';
import loc, { format } from 'owa-localize';
import {
    junkMailHeader,
    singleItem,
    noneOrMultipleItems,
    recoverItemsDeletedFromThisFolder,
    goBackToDeletedItems,
    deletedItemsFolderWarning,
    attachmentsSearchFolderWarning,
    unsyncedFolderWarning,
} from './mailListActionRow.locstring.json';
import MailListActionHeaderRow, {
    type MailListActionHeaderRowProps,
} from '../components/listHeaders/MailListActionHeaderRow';
import folderStore from 'owa-folders';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline - Migrating deprecated icons: https://outlookweb.visualstudio.com/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/9297/Migrating-deprecated-icons-in-client-web
 *	> 'ControlIcons' import from owa-control-icons is restricted. Use JSX icons from @fluentui/react-icons, otherwise from owa-fluent-icons-svg if they can only be reference by string. */
import { type ControlIcons } from 'owa-control-icons';
import { default as DeleteArrowBack } from 'owa-fluent-icons-svg/lib/icons/DeleteArrowBackRegular';
import isDeletedItemsFolderFromAnyMailbox from 'owa-mail-store/lib/utils/isDeletedItemsFolderFromAnyMailbox';
import {
    ARCHIVE_DUMPSTER_DISTINGUISHED_ID,
    ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID,
} from 'owa-folders-constants';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { selectFolder } from 'owa-mail-folder-forest-actions';
import {
    type TableView,
    getFocusedFilterForTable,
    type MailFolderTableQuery,
    isCatchUpFilter,
    isHeadlinesFilter,
} from 'owa-mail-list-store';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getMailboxInfoFromFolderId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import { getAccountScopeUserSettings } from 'owa-session-store';
import type { LocalizedString } from 'owa-localize';
import type { UserSettingsStyleSelector } from './getStyleSelectorAsPerUserSettings';
import { RollupContainer } from 'owa-mail-rollup';
import isAttachmentsSearchFolder from 'owa-mail-store/lib/utils/isAttachmentsSearchFolder';
import { getItem, type LocalStorageKeys } from 'owa-local-storage';
import { shouldRenderColumnHeaders } from 'owa-mail-layout';
import { isCapabilityEnabled } from 'owa-capabilities';
import { recoverableDeleteCapability } from 'owa-capabilities-definitions/lib/recoverableDeleteCapability';
import type DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import { isCopilotHeadlinesStarted } from 'owa-mail-copilot-headlines/lib/utils/isCopilotHeadlinesStarted';
import type { RollupContainerProps } from 'owa-mail-rollup';

export default function initializeThirdRow(
    dumpsterFolderDistinguishedName: DistinguishedFolderIdName,
    dumpsterFolderId: string,
    deletedItemsFolderDistinguishedId: DistinguishedFolderIdName,
    folderId: string,
    treeType: string,
    styleSelectorAsPerUserSettings: UserSettingsStyleSelector | undefined,
    mailListThirdRowCssClass: string,
    tableView?: TableView
):
    | React.FunctionComponentElement<MailListActionHeaderRowProps>
    | (false | React.FunctionComponentElement<MailListActionHeaderRowProps>)[]
    | React.FunctionComponentElement<RollupContainerProps>
    | null {
    const mailboxInfo = getMailboxInfoFromFolderId(folderId);
    const isDeletedTable = isDeletedItemsFolderFromAnyMailbox(folderId);
    const recoverDeletedItemsEnabled = isCapabilityEnabled(
        recoverableDeleteCapability,
        mailboxInfo
    );
    const isConsumerAccount = isConsumer(undefined, mailboxInfo);
    const isItemView = tableView?.tableQuery?.listViewType === 1;
    /**
     * Only show junk email folder header if we're in "Junk Email" folder. In
     * Cloud Cache mailboxes, the junk email folder is called "Spam" so we
     * shouldn't show it.
     */

    if (folderIdToName(folderId) == 'junkemail' && !IsShadowMailboxUser(mailboxInfo)) {
        const retentionPolicyTags = getAccountScopeUserSettings(mailboxInfo).RetentionPolicyTags;
        let showJunkMailThirdRow = isConsumerAccount;

        let retentionDays = 10; // Default

        if (retentionPolicyTags) {
            for (const policy of retentionPolicyTags) {
                if (
                    policy.DisplayName === 'JunkMail' ||
                    policy.DisplayName === 'Junk Email' ||
                    policy.DisplayName === 'Consumer Junk Email'
                ) {
                    retentionDays = policy.RetentionPeriod;
                    showJunkMailThirdRow = true;
                    break;
                }
            }
        }

        // Do not display third row junk mail header if enterprise account does not have a retention policy for junk
        if (showJunkMailThirdRow) {
            return React.createElement(
                MailListActionHeaderRow,
                {
                    alternativeIcon: 'Warning',
                    containerCssClass: mailListThirdRowCssClass,
                    text: format(loc(junkMailHeader), retentionDays),
                    mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
                    isItemView,
                },
                null /* children */
            );
        }
    }

    if (isAttachmentsSearchFolder(folderId)) {
        return React.createElement(
            MailListActionHeaderRow,
            {
                alternativeIcon: 'Warning',
                containerCssClass: mailListThirdRowCssClass,
                text: format(loc(attachmentsSearchFolderWarning)),
                mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
                isItemView,
            },
            null /* children */
        );
    }

    // Check if folderId is a folder that isn't syncing for the user (disabled IMAP)
    const unsyncedDistinguishedFolderIdNamesStorageKey: LocalStorageKeys = `UnsyncedDistinguishedFolderIdNames-${mailboxInfo.mailboxSmtpAddress}`;
    const unsyncedDistinguishedFolderIdNames = (
        getItem(window, unsyncedDistinguishedFolderIdNamesStorageKey) ?? ''
    ).split(',');
    const isUnsyncedFolder = unsyncedDistinguishedFolderIdNames.includes(folderIdToName(folderId));
    if (isUnsyncedFolder) {
        return React.createElement(
            MailListActionHeaderRow,
            {
                alternativeIcon: 'Warning',
                containerCssClass: mailListThirdRowCssClass,
                text: loc(unsyncedFolderWarning),
                mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
                isItemView,
            },
            null /* children */
        );
    }

    const isDumpsterTable = dumpsterFolderId === folderId;

    if (isDeletedTable && recoverDeletedItemsEnabled) {
        // Dumpster folder is lazily loaded and fetched,
        // and hence may not be ready
        const dumpsterFolder = folderStore().folderTable.get(dumpsterFolderId);
        const dumpsterTotalCount = dumpsterFolder ? dumpsterFolder.totalMessageCount : -1;
        let dumpsterLinkNumItemsString;
        let showLoadingSpinner = false;
        const warningText = loc(deletedItemsFolderWarning);

        if (dumpsterTotalCount < 0) {
            showLoadingSpinner = true;
        } else if (dumpsterTotalCount === 1) {
            dumpsterLinkNumItemsString = format(loc(singleItem), dumpsterTotalCount);
        } else {
            dumpsterLinkNumItemsString = format(loc(noneOrMultipleItems), dumpsterTotalCount);
        }

        let dumpsterLinkString = loc(recoverItemsDeletedFromThisFolder);
        // Currently, we don't not show the number for archive mailbox because
        // the count gets updated by hierarchy notifications
        if (!showLoadingSpinner && treeType === 'primaryFolderTree') {
            dumpsterLinkString = format(
                '{0} {1}{2}{3}' as any as LocalizedString, // Ideally this should come from locstring.json
                loc(recoverItemsDeletedFromThisFolder),
                '(',
                dumpsterLinkNumItemsString,
                ')'
            );
        }
        const dumpsterItemsFolderId = folderNameToId(dumpsterFolderDistinguishedName, mailboxInfo);
        return [
            isConsumerAccount &&
                React.createElement(
                    MailListActionHeaderRow,
                    {
                        text: warningText,
                        showLoadingSpinner,
                        mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
                        alternativeIcon: 'Warning',
                        isItemView,
                    },
                    null /* children */
                ),
            React.createElement(
                MailListActionHeaderRow,
                {
                    containerCssClass: mailListThirdRowCssClass,
                    text: dumpsterLinkString,
                    onClick: dumpsterItemsFolderId
                        ? () => goToDumpster(dumpsterFolderDistinguishedName, dumpsterItemsFolderId)
                        : undefined,
                    showLoadingSpinner,
                    mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
                    alternativeIcon: DeleteArrowBack,
                    isItemView,
                },
                null /* children */
            ),
        ];
    } else if (isDumpsterTable) {
        const deletedItemsFolderId = folderNameToId(deletedItemsFolderDistinguishedId, mailboxInfo);
        return React.createElement(
            MailListActionHeaderRow,
            {
                alternativeIcon: 'ChevronLeft',
                containerCssClass: mailListThirdRowCssClass,
                text: loc(goBackToDeletedItems),
                /* brand new accounts don't have this folder so don't pass in an on click method so
               it renders as plain text */
                onClick: deletedItemsFolderId
                    ? () =>
                          goToDeletedItems(deletedItemsFolderDistinguishedId, deletedItemsFolderId)
                    : undefined,
                mailListHeaderStylesAsPerUserSettings: styleSelectorAsPerUserSettings,
                showBackArrow: true,
                isItemView,
            },
            null /* children */
        );
    }

    if (
        tableView &&
        !isCopilotHeadlinesStarted() &&
        !isCatchUpFilter(tableView.tableQuery as MailFolderTableQuery) &&
        !isHeadlinesFilter(tableView.tableQuery as MailFolderTableQuery) &&
        folderIdToName(folderId) === 'inbox' &&
        !shouldRenderColumnHeaders(tableView.id)
    ) {
        return React.createElement(RollupContainer, {
            focusedViewFilter: getFocusedFilterForTable(tableView),
            tableView,
            shouldShowRollupOverride: true,
        });
    }

    return null;
}

function goToDumpster(distinguishedFolderName: string, distinguishedFolderId: string) {
    const treeType =
        distinguishedFolderName === ARCHIVE_DUMPSTER_DISTINGUISHED_ID
            ? 'archiveFolderTree'
            : 'primaryFolderTree';
    selectFolder(distinguishedFolderId, treeType, 'Dumpster');
}

function goToDeletedItems(distinguishedFolderName: string, distinguishedFolderId: string) {
    const treeType =
        distinguishedFolderName === ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID
            ? 'archiveFolderTree'
            : 'primaryFolderTree';
    selectFolder(distinguishedFolderId, treeType, 'Dumpster');
}
